import { createContext, useContext, useEffect, useRef, useState } from "react";
import { resolveLocalized, LOCALE_CATALOGUE } from "@shared/i18n";
import { makeT, type Translate, type UiKey } from "@shared/guest-strings";
import type {
  GuestApp,
  GuestContent,
  GuestInstagram,
  GuestPromo,
  GuestSection,
  GuestStation,
  GuestWeather,
  GuestWifi,
} from "@shared/types/guest";
import type { InstagramPost } from "@shared/types/foyer";
import { track } from "./analytics";
import { openOnTv } from "./api";

export type Localize = (v: unknown) => string;

export function makeLocalize(locale: string, defaultLocale: string): Localize {
  return (v: unknown) => resolveLocalized(v as never, locale, defaultLocale);
}

/** Fire a callback once, the first time an element scrolls into view. */
function useInViewOnce(onSeen: () => void): (el: HTMLElement | null) => void {
  const done = useRef(false);
  const cb = useRef(onSeen);
  cb.current = onSeen;
  return (el: HTMLElement | null) => {
    if (!el || done.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !done.current) {
          done.current = true;
          cb.current();
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
  };
}

export function Hero(props: {
  content: GuestContent;
  roomLabel: string | null;
  L: Localize;
}) {
  const { branding } = props.content;
  return (
    <header className="hero">
      {branding.heroUrl && (
        <div
          className="hero__bg"
          style={{ backgroundImage: `url(${branding.heroUrl})` }}
        />
      )}
      <div className="hero__scrim" />
      <div className="hero__body">
        {branding.logoUrl ? (
          <img className="hero__logo" src={branding.logoUrl} alt="" />
        ) : (
          <h1 className="hero__name">{branding.premiseName}</h1>
        )}
        {branding.logoUrl && (
          <div className="hero__name hero__name--sub">
            {branding.premiseName}
          </div>
        )}
        {props.roomLabel && (
          <span className="hero__room">{props.roomLabel}</span>
        )}
      </div>
    </header>
  );
}

export function WelcomeCard(props: { title: string; body: string }) {
  return (
    <section className="card card--welcome">
      <h2 className="card__title">{props.title}</h2>
      <p className="card__body">{props.body}</p>
    </section>
  );
}

export function PromoCard(props: { promo: GuestPromo; L: Localize }) {
  const { promo, L } = props;
  return (
    <section className="card card--promo">
      {promo.imageUrl && (
        <img className="promo__img" src={promo.imageUrl} alt="" />
      )}
      <div className="promo__text">
        {promo.title && <div className="promo__kicker">{L(promo.title)}</div>}
        <p className="card__body">{L(promo.body)}</p>
      </div>
    </section>
  );
}

/** Render a value; linkify tel:/mailto:/http targets and track clicks. */
function ItemValue(props: { value: string; section: string }) {
  const v = props.value.trim();
  const href = toHref(v);
  if (!href) return <span className="item__value">{props.value}</span>;
  return (
    <a
      className="item__value item__value--link"
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      onClick={() => track("click", `${props.section}:${href.split(":")[0]}`)}
    >
      {props.value}
    </a>
  );
}

function toHref(v: string): string | null {
  if (/^https?:\/\//i.test(v)) return v;
  if (/^[\w.+-]+@[\w.-]+\.\w+$/.test(v)) return `mailto:${v}`;
  if (/^[+()\d][\d\s()+-]{5,}$/.test(v)) return `tel:${v.replace(/\s+/g, "")}`;
  return null;
}

export function SectionCard(props: { section: GuestSection; L: Localize }) {
  const { section, L } = props;
  const ref = useInViewOnce(() => track("section_view", section.id));
  return (
    <section className="card" ref={ref}>
      <h2 className="card__title">{L(section.title)}</h2>
      <dl className="items">
        {section.items.map((it, i) => (
          <div className="item" key={i}>
            <dt className="item__label">{L(it.label)}</dt>
            <dd className="item__valuewrap">
              <ItemValue value={L(it.value)} section={section.id} />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function WifiCard(props: {
  wifi: GuestWifi;
  L: Localize;
  t: Translate;
}) {
  const { wifi, t } = props;
  const ref = useInViewOnce(() => track("section_view", "wifi"));
  return (
    <section className="card card--wifi" ref={ref}>
      <h2 className="card__title">{t("wifi")}</h2>
      <dl className="items">
        <div className="item">
          <dt className="item__label">{t("network")}</dt>
          <dd className="item__valuewrap">
            <span className="item__value">{wifi.ssid}</span>
          </dd>
        </div>
        {wifi.password && (
          <div className="item">
            <dt className="item__label">{t("password")}</dt>
            <dd className="item__valuewrap">
              <span className="item__value item__value--mono">
                {wifi.password}
              </span>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

/* ---------- Apps ---------- */

/** Launch URL for an app tile on the guest's phone.
 *  1. A resolved `launchUrl` - a custom scheme ("viaplay://") or an https App
 *     Link ("https://www.netflix.com/") - opens the installed app directly.
 *  2. Otherwise, on Android, a MAIN/LAUNCHER package intent (opens the installed
 *     app, else the Play Store). Note: the catalogue slug is the Android-TV
 *     package, so this only matches when the guest has that exact package.
 *  Null → nothing to launch (e.g. desktop, or no ids). */
function appLaunchUrl(app: {
  launchUrl?: string;
  androidSlug?: string;
}): string | null {
  if (app.launchUrl) return app.launchUrl;
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (/Android/.test(ua) && app.androidSlug) {
    return (
      `intent://#Intent;action=android.intent.action.MAIN;` +
      `category=android.intent.category.LAUNCHER;package=${app.androidSlug};end`
    );
  }
  return null;
}

/** An https launch URL should open in a new tab so the companion page survives
 *  when the target opens the website (rather than the app). Schemes/intents
 *  don't navigate, so they need no target. */
function launchTarget(url: string): string | undefined {
  return /^https?:/i.test(url) ? "_blank" : undefined;
}

/** Google Play listing for an app - the deliberate fallback for the Live TV
 *  card (the streaming app a guest's phone likely won't have installed yet). */
function playStoreUrl(app: { androidSlug?: string }): string | null {
  return app.androidSlug
    ? `https://play.google.com/store/apps/details?id=${app.androidSlug}`
    : null;
}

/* ---------- "Open on this device / on your TV" ---------- */

/** Open `url` on the guest's own phone. https links get a new tab (so the
 *  companion page survives when the target falls back to the website); schemes
 *  and Android intents don't navigate, so they go through the current frame. */
function openHere(url: string): void {
  if (/^https?:/i.test(url)) window.open(url, "_blank", "noopener,noreferrer");
  else window.location.href = url;
}

type LaunchPhase = "idle" | "sending" | "sent" | "error";

interface OpenTargetApi {
  /** Whether the room has a TV to launch on - false → callers open directly. */
  canOpenOnTv: boolean;
  /** Ask the guest where to open `app`; `deviceUrl` is the phone-side target
   *  (null when this app can't be opened on the phone at all). */
  prompt: (app: GuestApp, deviceUrl: string | null) => void;
}

const OpenTargetCtx = createContext<OpenTargetApi>({
  canOpenOnTv: false,
  prompt: () => {},
});

/**
 * Hosts the "where do you want to open it?" sheet for every app tile below it.
 *
 * A tap on a tile is ambiguous once the room has a TV: the guest may want the
 * app on the phone in their hand, or up on the big screen. Rather than guess,
 * ask - one sheet, two plain choices, fully localized. Rooms without a bound TV
 * never see it (`canOpenOnTv` false → the tile opens on the phone as before).
 */
export function OpenTargetProvider(props: {
  canOpenOnTv: boolean;
  t: Translate;
  children: React.ReactNode;
}) {
  const { canOpenOnTv, t, children } = props;
  const [pending, setPending] = useState<{
    app: GuestApp;
    deviceUrl: string | null;
  } | null>(null);
  const [phase, setPhase] = useState<LaunchPhase>("idle");
  const [errorKey, setErrorKey] = useState<UiKey>("tv_failed");

  const close = () => {
    setPending(null);
    setPhase("idle");
  };

  const prompt = (app: GuestApp, deviceUrl: string | null) => {
    setPhase("idle");
    setErrorKey("tv_failed");
    setPending({ app, deviceUrl });
  };

  const chooseDevice = () => {
    if (!pending) return;
    track("click", `app:${pending.app.id}:device`);
    if (pending.deviceUrl) openHere(pending.deviceUrl);
    close();
  };

  const chooseTv = async () => {
    if (!pending) return;
    track("click", `app:${pending.app.id}:tv`);
    setPhase("sending");
    const res = await openOnTv(pending.app.id);
    if (res.ok) {
      setPhase("sent");
      // Let the guest read the confirmation, then get out of their way.
      window.setTimeout(close, 1600);
      return;
    }
    setErrorKey(res.code === "tv_offline" ? "tv_offline" : "tv_failed");
    setPhase("error");
  };

  return (
    <OpenTargetCtx.Provider value={{ canOpenOnTv, prompt }}>
      {children}
      {pending && (
        <OpenTargetSheet
          app={pending.app}
          canOpenHere={!!pending.deviceUrl}
          phase={phase}
          errorKey={errorKey}
          t={t}
          onDevice={chooseDevice}
          onTv={() => void chooseTv()}
          onClose={close}
        />
      )}
    </OpenTargetCtx.Provider>
  );
}

function OpenTargetSheet(props: {
  app: GuestApp;
  canOpenHere: boolean;
  phase: LaunchPhase;
  errorKey: UiKey;
  t: Translate;
  onDevice: () => void;
  onTv: () => void;
  onClose: () => void;
}) {
  const { app, canOpenHere, phase, errorKey, t, onDevice, onTv, onClose } =
    props;
  const firstRef = useRef<HTMLButtonElement | null>(null);
  const titleId = `open-target-${app.id}`;

  // Move focus into the sheet and let Escape dismiss it - the sheet is modal,
  // so keyboard and screen-reader users must not be left behind it.
  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const busy = phase === "sending" || phase === "sent";
  return (
    <div className="sheet" role="presentation" onClick={onClose}>
      <div
        className="sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet__head">
          <AppBadge app={app} className="sheet__badge" />
          <div className="sheet__heading">
            <h2 className="sheet__title" id={titleId}>
              {app.name}
            </h2>
            <p className="sheet__sub">{t("open_where")}</p>
          </div>
        </div>

        {phase === "error" && (
          <p className="sheet__error" role="alert">
            {t(errorKey)}
          </p>
        )}

        <div className="sheet__actions">
          {canOpenHere && (
            <button
              className="sheet__btn"
              ref={firstRef}
              onClick={onDevice}
              disabled={busy}
            >
              {t("open_on_device")}
            </button>
          )}
          <button
            className="sheet__btn sheet__btn--primary"
            ref={canOpenHere ? undefined : firstRef}
            onClick={onTv}
            disabled={busy}
          >
            {phase === "sending"
              ? t("tv_opening")
              : phase === "sent"
                ? t("tv_opened")
                : t("open_on_tv")}
          </button>
          <button className="sheet__cancel" onClick={onClose}>
            {t("cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Brand-ink colour for the wordmark on a light tile (mirrors the TV: white
 *  brand colours fall back to the text colour, then near-black). */
function brandInk(app: GuestApp): string {
  const notWhite = (c?: string) =>
    !!c && !["#fff", "#ffffff", "white"].includes(c.toLowerCase());
  return notWhite(app.color)
    ? app.color
    : notWhite(app.text)
      ? app.text
      : "#1a1a1a";
}

function AppBadge(props: { app: GuestApp; className?: string }) {
  const { app, className } = props;
  return (
    <span className={`apptile__badge ${className ?? ""}`}>
      {app.logoUrl ? (
        <img className="apptile__logo" src={app.logoUrl} alt={app.name} />
      ) : (
        <span className="apptile__word" style={{ color: brandInk(app) }}>
          {app.wordmark || app.name}
        </span>
      )}
    </span>
  );
}

export function AppsCard(props: { apps: GuestApp[]; t: Translate }) {
  const { apps, t } = props;
  const ref = useInViewOnce(() => track("section_view", "apps"));
  if (!apps.length) return null;
  return (
    <section className="card" ref={ref}>
      <h2 className="card__title">{t("apps")}</h2>
      <div className="apps">
        {apps.map((a) => (
          <AppTile key={a.id} app={a} />
        ))}
      </div>
    </section>
  );
}

function AppTile(props: { app: GuestApp }) {
  const { app } = props;
  const url = appLaunchUrl(app);
  const { canOpenOnTv, prompt } = useContext(OpenTargetCtx);
  const inner = (
    <>
      <AppBadge app={app} />
      <span className="apptile__name">{app.name}</span>
    </>
  );
  // With a TV in the room the destination is a real choice - ask first. It's a
  // button, not a link: the tap opens a dialog, it doesn't navigate.
  if (canOpenOnTv) {
    return (
      <button
        className="apptile apptile--link"
        onClick={() => prompt(app, url)}
        aria-haspopup="dialog"
        aria-label={app.name}
      >
        {inner}
      </button>
    );
  }
  if (url) {
    return (
      <a
        className="apptile apptile--link"
        href={url}
        target={launchTarget(url)}
        rel="noopener noreferrer"
        onClick={() => track("click", `app:${app.id}`)}
        aria-label={app.name}
      >
        {inner}
      </a>
    );
  }
  return <div className="apptile">{inner}</div>;
}

/** "Live TV" hero card at the top of the stack. Presentation is about live TV
 *  (not the underlying app brand); `app` is only the launch target - a tap opens
 *  the streaming app via its scheme, else its Play Store listing. */
export function LiveTvCard(props: { app: GuestApp; t: Translate }) {
  const { app, t } = props;
  const url = appLaunchUrl(app) ?? playStoreUrl(app);
  const { canOpenOnTv, prompt } = useContext(OpenTargetCtx);
  const inner = (
    <>
      <span className="livetv__icon" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          width="26"
          height="26"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* antenna + screen */}
          <path d="m8 3 4 4 4-4" />
          <rect x="2" y="7" width="20" height="13" rx="2.5" />
          {/* play glyph */}
          <path
            d="M10.5 11.2 15 13.5l-4.5 2.3z"
            fill="currentColor"
            stroke="none"
          />
        </svg>
        <span className="livetv__pulse" />
      </span>
      <span className="livetv__meta">
        <span className="livetv__title">{t("live_tv")}</span>
        <span className="livetv__sub">{t("live_tv_sub")}</span>
      </span>
      {url && (
        <span className="livetv__chev" aria-hidden="true">
          ›
        </span>
      )}
    </>
  );
  if (canOpenOnTv) {
    return (
      <button
        className="livetv livetv--link"
        onClick={() => prompt(app, url)}
        aria-haspopup="dialog"
        aria-label={t("live_tv")}
      >
        {inner}
      </button>
    );
  }
  if (url) {
    return (
      <a
        className="livetv livetv--link"
        href={url}
        target={launchTarget(url)}
        rel="noopener noreferrer"
        onClick={() => track("click", "livetv")}
        aria-label={t("live_tv")}
      >
        {inner}
      </a>
    );
  }
  return <div className="livetv">{inner}</div>;
}

/* ---------- Radio ---------- */

/** In-page audio playback: tap a station to stream its `streamUrl`, tap again
 *  (or another) to stop/switch. Leaving the page stops it. Mirrors the TV. */
function useRadioPlayback(stations: Array<{ id: string; streamUrl?: string }>) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggle = (id: string | null | undefined) => {
    if (!id) return;
    setPlayingId((cur) => (cur === id ? null : id));
  };

  const playingUrl = playingId
    ? stations.find((s) => s.id === playingId)?.streamUrl
    : undefined;

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (playingUrl) {
      if (el.src !== playingUrl) el.src = playingUrl;
      void el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [playingUrl]);

  useEffect(
    () => () => {
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.removeAttribute("src");
      }
    },
    []
  );

  return { playingId, toggle, audioRef };
}

interface NowPlayingInfo {
  program?: string;
  song?: string;
}

/** Poll Sveriges Radio "now playing" for stations that carry a `metadataId`. */
function useRadioNowPlaying(
  ids: Array<string | undefined>
): Record<string, NowPlayingInfo> {
  const [data, setData] = useState<Record<string, NowPlayingInfo>>({});
  const key = [...new Set(ids.filter((x): x is string => !!x))]
    .sort()
    .join(",");
  useEffect(() => {
    if (!key) {
      setData({});
      return;
    }
    let alive = true;
    const poll = async () => {
      try {
        const res = await fetch(
          `/api/frontlayer/v1/radio/now-playing?ids=${encodeURIComponent(key)}`
        );
        if (!res.ok) return;
        const json = (await res.json()) as Record<string, NowPlayingInfo>;
        if (alive) setData(json);
      } catch {
        /* keep last good value on a network blip */
      }
    };
    void poll();
    const t = window.setInterval(poll, 45_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [key]);
  return data;
}

export function RadioCard(props: {
  stations: GuestStation[];
  L: Localize;
  t: Translate;
}) {
  const { stations, L, t } = props;
  const ref = useInViewOnce(() => track("section_view", "radio"));
  const { playingId, toggle, audioRef } = useRadioPlayback(stations);
  const now = useRadioNowPlaying(stations.map((s) => s.metadataId));
  if (!stations.length) return null;
  return (
    <section className="card" ref={ref}>
      <h2 className="card__title">{t("radio")}</h2>
      <ul className="stations">
        {stations.map((s) => {
          const live = s.metadataId ? now[s.metadataId] : undefined;
          const sub =
            (live?.song && live?.program
              ? `${live.program} - ${live.song}`
              : live?.song || live?.program) ||
            (s.nowPlaying ? L(s.nowPlaying) : "") ||
            (s.tag ? L(s.tag) : "") ||
            s.freq ||
            "";
          const playing = playingId === s.id;
          const playable = !!s.streamUrl;
          return (
            <li className="station" key={s.id}>
              {playable ? (
                <button
                  className={`station__play ${playing ? "station__play--on" : ""}`}
                  onClick={() => {
                    toggle(s.id);
                    if (!playing) track("click", `radio_play:${s.id}`);
                  }}
                  aria-label={
                    playing
                      ? `${t("stop")} ${s.name}`
                      : `${t("play")} ${s.name}`
                  }
                >
                  {playing ? "◼" : "▶"}
                </button>
              ) : (
                <span className="station__play station__play--muted">♪</span>
              )}
              <span className="station__meta">
                <span className="station__name">{s.name}</span>
                {sub && <span className="station__sub">{sub}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      <audio ref={audioRef} preload="none" />
    </section>
  );
}

/* ---------- Instagram ---------- */

/** Fetch a premise's recent Instagram posts for the feed grid. */
function useInstagramMedia(
  premiseId: string | undefined,
  count: number,
  enabled: boolean
): { posts: InstagramPost[]; loading: boolean } {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(enabled);
  useEffect(() => {
    if (!enabled || !premiseId) {
      setPosts([]);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    fetch(
      `/api/frontlayer/v1/instagram/media?premiseId=${encodeURIComponent(
        premiseId
      )}&limit=${count}`
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j: { posts?: InstagramPost[] }) => {
        if (alive) setPosts(j.posts ?? []);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [premiseId, count, enabled]);
  return { posts, loading };
}

export function InstagramCard(props: {
  instagram: GuestInstagram;
  premiseId?: string;
  L: Localize;
  t: Translate;
}) {
  const { instagram: ig, premiseId, L, t } = props;
  const ref = useInViewOnce(() => track("section_view", "instagram"));
  const wantsFeed = ig.display === "feed" || ig.display === "both";
  const { posts } = useInstagramMedia(premiseId, ig.feedCount, wantsFeed);
  const profile = `https://instagram.com/${ig.handle}`;
  return (
    <section className="card card--ig" ref={ref}>
      <h2 className="card__title">{t("instagram")}</h2>
      {ig.caption && <p className="card__body ig__caption">{L(ig.caption)}</p>}
      {wantsFeed && posts.length > 0 && (
        <div className="ig__grid">
          {posts.map((p) => (
            <a
              key={p.id}
              className="ig__cell"
              href={p.permalink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("click", `instagram_post:${p.id}`)}
            >
              <img
                src={p.thumbnailUrl || p.mediaUrl}
                alt={p.caption ? p.caption.slice(0, 80) : ""}
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}
      <a
        className="ig__follow"
        href={profile}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("click", "instagram_follow")}
      >
        {t("follow")} @{ig.handle}
      </a>
    </section>
  );
}

/* ---------- Weather ---------- */
interface WeatherWire {
  current: { tempC: number; symbol: string };
  days: Array<{ date: string; minC: number; maxC: number; symbol: string }>;
}

function useWeather(lat: number, lng: number): WeatherWire | null {
  const [data, setData] = useState<WeatherWire | null>(null);
  useEffect(() => {
    let alive = true;
    fetch(`/api/frontlayer/v1/weather?lat=${lat}&lon=${lng}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j: WeatherWire) => alive && setData(j))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [lat, lng]);
  return data;
}

/** Map a met.no symbol family to an emoji glyph (self-contained, no icon kit). */
function weatherEmoji(sym: string): string {
  if (!sym) return "🌡️";
  if (sym.includes("thunder")) return "⛈️";
  if (sym.includes("sleet")) return "🌨️";
  if (sym.includes("snow")) return "❄️";
  if (sym.includes("rain") || sym.includes("shower")) return "🌧️";
  if (sym.includes("fog")) return "🌫️";
  if (sym === "clearsky" || sym === "fair") return "☀️";
  if (sym.includes("partlycloudy")) return "⛅";
  if (sym.includes("cloud")) return "☁️";
  return "🌡️";
}

export function WeatherCard(props: {
  weather: GuestWeather;
  locale: string;
  t: Translate;
}) {
  const { weather, locale, t } = props;
  const ref = useInViewOnce(() => track("section_view", "weather"));
  const data = useWeather(weather.lat, weather.lng);
  if (!data) return null;
  const toUnit = (c: number) =>
    weather.units === "f" ? Math.round((c * 9) / 5 + 32) : c;
  const deg = weather.units === "f" ? "°F" : "°C";
  const weekday = (iso: string) => {
    const d = new Date(`${iso}T12:00:00Z`);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(locale, { weekday: "short" });
  };
  return (
    <section className="card card--weather" ref={ref}>
      <h2 className="card__title">{t("weather")}</h2>
      <div className="weather__now">
        <span className="weather__icon">
          {weatherEmoji(data.current.symbol)}
        </span>
        <span className="weather__temp">
          {toUnit(data.current.tempC)}
          {deg}
        </span>
      </div>
      {data.days.length > 0 && (
        <div className="weather__days">
          {data.days.map((d) => (
            <div className="weather__day" key={d.date}>
              <span className="weather__dow">{weekday(d.date)}</span>
              <span className="weather__dayicon">{weatherEmoji(d.symbol)}</span>
              <span className="weather__range">
                {toUnit(d.maxC)}° / {toUnit(d.minC)}°
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function LanguageSwitcher(props: {
  locales: string[];
  value: string;
  onChange: (loc: string) => void;
}) {
  if (props.locales.length < 2) return null;
  const t = makeT(props.value);
  const label = (code: string) =>
    LOCALE_CATALOGUE.find((l) => l.code === code)?.native ?? code.toUpperCase();
  return (
    <div className="langs" role="group" aria-label={t("language")}>
      {props.locales.map((code) => (
        <button
          key={code}
          className={`lang ${code === props.value ? "lang--on" : ""}`}
          aria-pressed={code === props.value}
          onClick={() => props.onChange(code)}
        >
          {label(code)}
        </button>
      ))}
    </div>
  );
}

/** Blend a hex colour with white/black at `alpha` → an `rgba()` string, so we
 *  can derive card + hairline tints from the scheme ink without extra tokens. */
function tint(hex: string | undefined, alpha: number): string | null {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** Apply premise branding to CSS custom properties + the theme-color meta, so
 *  the web reflects the premise's scheme palette and typography (not the
 *  generic default theme). */
export function useBranding(content: GuestContent | null) {
  useEffect(() => {
    if (!content) return;
    const root = document.documentElement;
    const b = content.branding;
    const set = (k: string, v: string | null | undefined) => {
      if (v) root.style.setProperty(k, v);
    };
    set("--accent", b.accent);
    set("--bg", b.bg);
    set("--ink", b.ink);
    // Secondary ink → muted; scheme rule (or an ink tint) → card border; a soft
    // ink tint → card fill. Falls back gracefully when a token is absent.
    set("--muted", b.inkDim ?? tint(b.ink, 0.62));
    set("--card-border", b.rule ?? tint(b.ink, 0.12));
    set("--card", tint(b.ink, 0.06));
    set("--font-sans", b.fontSans);
    set("--font-serif", b.fontSerif ?? b.fontSans);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && b.bg) meta.setAttribute("content", b.bg);
    document.title = b.premiseName || "Guest";
  }, [content]);
}
